import {
  createDefaultUnmanagedBusinessAutoRegisterCondition,
  createDefaultUnmanagedBusinessAutoRegisterSettingItem,
  UnmanagedBusinessAutoRegisterCondition,
  UnmanagedBusinessAutoRegisterConditionSearchType,
  UnmanagedBusinessAutoRegisterField,
  UnmanagedBusinessAutoRegisterRegisterUnit,
  UnmanagedBusinessAutoRegisterSettingConditionItem,
  UnmanagedBusinessAutoRegisterSettingItem,
  UnmanagedBusinessAutoRegisterOptionalRegisterItem,
} from '@/coreDomainsModule/constants/unmanagedBusinessAutoRegister';
import { defineStore } from 'pinia';
import { reactive } from 'vue';

/**
 * 未管理取引設定ページの表示モード
 */
export type SettingPageMode = 'list' | 'create' | 'edit';

/**
 * 未管理取引設定ページで利用するUI状態
 */
export interface SettingUnmanagedBusinessState {
  condition: UnmanagedBusinessAutoRegisterCondition;
  updateAt?: number;
  initialized: boolean;
  isSaving: boolean;
  mode: SettingPageMode;
  editingSettingId?: string;
  formSetting: UnmanagedBusinessAutoRegisterSettingItem;
  openedSettingIds: string[];
}

/**
 * 未管理取引設定ページの初期状態を生成する
 */
const createDefaultState = (): SettingUnmanagedBusinessState => {
  return {
    condition: createDefaultUnmanagedBusinessAutoRegisterCondition(),
    updateAt: undefined,
    initialized: false,
    isSaving: false,
    mode: 'list',
    editingSettingId: undefined,
    formSetting: createDefaultUnmanagedBusinessAutoRegisterSettingItem(),
    openedSettingIds: [],
  };
};

/**
 * 未管理取引設定ページ用Store
 */
export const useSettingUnmanagedBusinessStore = defineStore('settingUnmanagedBusinessStore', () => {
  const state = reactive<SettingUnmanagedBusinessState>(createDefaultState());

  /**
   * Storeの状態を初期化する
   */
  const resetState = (): void => {
    const defaultState = createDefaultState();
    state.condition = defaultState.condition;
    state.updateAt = defaultState.updateAt;
    state.initialized = defaultState.initialized;
    state.isSaving = defaultState.isSaving;
    state.mode = defaultState.mode;
    state.editingSettingId = defaultState.editingSettingId;
    state.formSetting = defaultState.formSetting;
    state.openedSettingIds = defaultState.openedSettingIds;
  };

  /**
   * 初期化状態を更新する
   * @param initialized - 初期化済みフラグ
   */
  const setInitialized = (initialized: boolean): void => {
    state.initialized = initialized;
  };

  /**
   * 保存中状態を更新する
   * @param isSaving - 保存中フラグ
   */
  const setIsSaving = (isSaving: boolean): void => {
    state.isSaving = isSaving;
  };

  /**
   * 画面モードを更新する
   * @param mode - 画面モード
   */
  const setMode = (mode: SettingPageMode): void => {
    state.mode = mode;
  };

  /**
   * 編集対象IDを更新する
   * @param settingId - 編集対象の設定ID
   */
  const setEditingSettingId = (settingId: string | undefined): void => {
    state.editingSettingId = settingId;
  };

  /**
   * 設定条件全体を更新する
   * @param condition - 設定条件
   */
  const setCondition = (condition: UnmanagedBusinessAutoRegisterCondition): void => {
    state.condition = condition;
  };

  /**
   * 更新時刻を更新する
   * @param updateAt - 更新時刻
   */
  const setUpdateAt = (updateAt: number | undefined): void => {
    state.updateAt = updateAt;
  };

  /**
   * 展開中設定ID一覧を更新する
   * @param openedSettingIds - 展開中設定ID一覧
   */
  const setOpenedSettingIds = (openedSettingIds: string[]): void => {
    state.openedSettingIds = openedSettingIds;
  };

  /**
   * 設定行の開閉状態を切り替える
   * @param settingId - 設定ID
   */
  const toggleOpenedSetting = (settingId: string): void => {
    if (state.openedSettingIds.includes(settingId)) {
      state.openedSettingIds = state.openedSettingIds.filter((openedSettingId) => openedSettingId !== settingId);
      return;
    }
    state.openedSettingIds = [...state.openedSettingIds, settingId];
  };

  /**
   * 編集中フォーム全体を更新する
   * @param formSetting - フォーム設定
   */
  const setFormSetting = (formSetting: UnmanagedBusinessAutoRegisterSettingItem): void => {
    state.formSetting = formSetting;
  };

  /**
   * 編集中フォームの条件一覧を更新する
   * @param conditions - 条件一覧
   */
  const setFormConditions = (conditions: UnmanagedBusinessAutoRegisterSettingConditionItem[]): void => {
    state.formSetting.conditions = conditions;
  };

  /**
   * 編集中フォームの設定名を更新する
   * @param settingName - 設定名
   */
  const setFormSettingName = (settingName: string): void => {
    state.formSetting.settingName = settingName;
  };

  /**
   * 編集中フォームのリードタイムを更新する
   * @param leadTime - リードタイム
   */
  const setFormLeadTime = (leadTime: number): void => {
    state.formSetting.registerContent.leadTime = leadTime;
  };

  /**
   * 編集中フォームの標準在庫指数minを更新する
   * @param stdInventoryIndicesMin - 標準在庫指数min
   */
  const setFormStdInventoryIndicesMin = (stdInventoryIndicesMin: number): void => {
    state.formSetting.registerContent.stdInventoryIndicesMin = stdInventoryIndicesMin;
  };

  /**
   * 編集中フォームの標準在庫指数maxを更新する
   * @param stdInventoryIndicesMax - 標準在庫指数max
   */
  const setFormStdInventoryIndicesMax = (stdInventoryIndicesMax: number): void => {
    state.formSetting.registerContent.stdInventoryIndicesMax = stdInventoryIndicesMax;
  };

  /**
   * 編集中フォームの条件検索方法を更新する
   * @param conditionIndex - 条件index
   * @param searchType - 検索方法
   */
  const setFormConditionSearchType = (conditionIndex: number, searchType: UnmanagedBusinessAutoRegisterConditionSearchType): void => {
    state.formSetting.conditions[conditionIndex].searchType = searchType;
  };

  /**
   * 編集中フォームの条件値を更新する
   * @param conditionIndex - 条件index
   * @param targetValue - 条件値
   */
  const setFormConditionTargetValue = (conditionIndex: number, targetValue: string): void => {
    state.formSetting.conditions[conditionIndex].targetValue = targetValue;
  };

  /**
   * 編集中フォームへ任意項目を追加する
   * @param optionalItem - 任意項目
   */
  const addFormOptionalItem = (optionalItem: UnmanagedBusinessAutoRegisterOptionalRegisterItem): void => {
    state.formSetting.registerContent.optionalItems.push(optionalItem);
  };

  /**
   * 編集中フォームから任意項目を削除する
   * @param optionalItemIndex - 削除対象index
   */
  const removeFormOptionalItem = (optionalItemIndex: number): void => {
    state.formSetting.registerContent.optionalItems.splice(optionalItemIndex, 1);
  };

  /**
   * 編集中フォームの任意項目フィールドを更新する
   * @param optionalItemIndex - 任意項目index
   * @param targetField - 対象フィールド
   */
  const setFormOptionalItemTargetField = (optionalItemIndex: number, targetField: UnmanagedBusinessAutoRegisterField): void => {
    state.formSetting.registerContent.optionalItems[optionalItemIndex].targetField = targetField;
  };

  /**
   * 編集中フォームの任意項目値を更新する
   * @param optionalItemIndex - 任意項目index
   * @param targetValue - 任意項目値
   */
  const setFormOptionalItemTargetValue = (optionalItemIndex: number, targetValue: string): void => {
    state.formSetting.registerContent.optionalItems[optionalItemIndex].targetValue = targetValue;
  };

  /**
   * 編集中フォームのリードタイム単位を更新する
   * @param leadTimeUnit - リードタイム単位
   */
  const setFormLeadTimeUnit = (leadTimeUnit: UnmanagedBusinessAutoRegisterRegisterUnit): void => {
    state.formSetting.registerContent.leadTimeUnit = leadTimeUnit;
  };

  /**
   * 編集中フォームの標準在庫指数単位を更新する
   * @param stdInventoryIndicesUnit - 標準在庫指数単位
   */
  const setFormStdInventoryIndicesUnit = (stdInventoryIndicesUnit: UnmanagedBusinessAutoRegisterRegisterUnit): void => {
    state.formSetting.registerContent.stdInventoryIndicesUnit = stdInventoryIndicesUnit;
  };

  return {
    state,
    resetState,
    setInitialized,
    setIsSaving,
    setMode,
    setEditingSettingId,
    setCondition,
    setUpdateAt,
    setOpenedSettingIds,
    toggleOpenedSetting,
    setFormSetting,
    setFormConditions,
    setFormSettingName,
    setFormLeadTime,
    setFormStdInventoryIndicesMin,
    setFormStdInventoryIndicesMax,
    setFormConditionSearchType,
    setFormConditionTargetValue,
    addFormOptionalItem,
    removeFormOptionalItem,
    setFormOptionalItemTargetField,
    setFormOptionalItemTargetValue,
    setFormLeadTimeUnit,
    setFormStdInventoryIndicesUnit,
  };
});
